<script setup lang="ts">
import type { Schemas } from '#shopware'
import type { ListingItemProduct, ListingItemTeaser } from '~/types/ListingItems'

type SliderItem = ListingItemProduct | ListingItemTeaser

const props = defineProps<{
    content: Schemas['CmsElementProductSlider']
}>()

const sliderItems = computed<SliderItem[]>(() => {
    const products: ListingItemProduct[] = (props.content?.data?.products ?? []).map((product, index) => ({
        pos: index,
        type: 'product',
        data: product,
    }))

    const teasers: ListingItemTeaser[] = Object.values(props.content?.data?.teaser ?? {}).map((t) => ({
        pos: t.position - 1,
        type: 'teaser',
        data: t,
    }))

    return [...teasers, ...products].sort((a, b) => a.pos - b.pos)
})

const itemListName = computed(() => {
    const fromConfig = props.content?.config?.item_list_name?.value?.trim()
    if (fromConfig) return fromConfig
    const fromTitle = props.content?.config?.title?.value?.trim()
    if (fromTitle) return fromTitle
    return 'Product slider'
})

const isFramed = computed(() => props.content?.config?.framed?.value === true)
const frameColor = computed(() => props.content?.config?.accentColor?.value ?? undefined)
const { borderStyle } = useCampaignTheme(frameColor)

const itemListId = computed(() => {
    if (props.content?.config?.promotion?.value?.promotion_id) {
        return props.content?.config.promotion.value.promotion_id
    }
    const fromTitle = props.content?.config?.title?.value?.trim()
    return fromTitle || 'product-slider'
})

if (props.content?.config?.promotion?.value || itemListName.value || itemListId.value) {
    provide('trackingData', {
        promotion: props.content?.config?.promotion?.value ?? null,
        itemListId: itemListId.value,
        itemListName: itemListName.value,
    })
}
</script>

<template>
    <div
        v-if="sliderItems.length > 0"
        :class="isFramed ? 'border border-neutral-focus p-4' : ''"
        :style="isFramed ? borderStyle : undefined"
        :data-cy="isFramed ? 'product-slider-framed' : undefined"
    >
        <ProductSlider
            :products="sliderItems"
            :product-qty="content?.config?.productQty"
            :progress-bar="content?.config?.progressBar?.value"
            :slider-id="itemListId"
            :slider-title="itemListName"
            :item-list-id="itemListId"
            :item-list-name="itemListName"
        />
    </div>
</template>
